import type {Filter, CheckboxFilter, SelectFilter} from '../types'
import type {SelectedFilter} from '@/src/core/domain/entities/catalog/filters'

export const NON_PROPERTY_FILTERS: string[] = ['price']

export type ParsedFilterQuery = {
  properties: Record<string, string[]>
  ranges: Record<string, {from: number; to: number}>
}

export function serializeFiltersToQuery(filters: Filter[]): Record<string, unknown> {
  const result: Record<string, unknown> = {}
  const properties: Record<string, unknown> = {}

  for (const filter of filters) {
    if (filter.type === 'range') {
      if (filter.selected_from !== null || filter.selected_to !== null) {
        const rangeValue = {
          from: filter.selected_from ?? filter.from,
          to: filter.selected_to ?? filter.to,
        }
        if (NON_PROPERTY_FILTERS.includes(filter.code)) {
          result[filter.code] = rangeValue
        } else {
          properties[filter.code] = rangeValue
        }
      }
    } else {
      const selectedValues = (filter as SelectFilter | CheckboxFilter).values
        .filter((v) => v.selected)
        .map((v) => v.value)

      if (selectedValues.length > 0) {
        if (NON_PROPERTY_FILTERS.includes(filter.code)) {
          result[filter.code] = selectedValues
        } else {
          properties[filter.code] = selectedValues
        }
      }
    }
  }

  if (Object.keys(properties).length > 0) {
    result.properties = properties
  }

  return result
}

export function deserializeFiltersFromQuery(rawFilterParam: unknown): ParsedFilterQuery {
  const result: ParsedFilterQuery = {properties: {}, ranges: {}}

  if (!rawFilterParam || typeof rawFilterParam !== 'object') return result

  const filterParam = rawFilterParam as Record<string, unknown>

  const propertiesParam = filterParam['properties']
  if (propertiesParam && typeof propertiesParam === 'object') {
    for (const [code, value] of Object.entries(propertiesParam as Record<string, unknown>)) {
      if (isRangeObject(value)) {
        result.ranges[code] = normalizeRange(value)
      } else {
        result.properties[code] = normalizeValueArray(value)
      }
    }
  }

  for (const [code, value] of Object.entries(filterParam)) {
    if (code === 'properties') continue
    if (isRangeObject(value)) {
      result.ranges[code] = normalizeRange(value)
    }
  }

  return result
}

export function applySelectedStateToFilters(raw: Filter[], parsed: ParsedFilterQuery): Filter[] {
  return raw.map((filter): Filter => {
    if (filter.type === 'range') {
      const range = parsed.ranges[filter.code]
      if (!range) return {...filter, selected_from: null, selected_to: null}
      return {...filter, selected_from: range.from, selected_to: range.to}
    }

    const selectedValues = parsed.properties[filter.code]
    if (!selectedValues?.length) {
      return {
        ...filter,
        values: filter.values.map((v) => ({...v, selected: false})),
      }
    }

    return {
      ...filter,
      values: filter.values.map((v) => ({...v, selected: selectedValues.includes(v.value)})),
    }
  })
}

export function deriveSelectedFilters(filters: Filter[]): SelectedFilter[] {
  const result: SelectedFilter[] = []

  for (const filter of filters) {
    const property = !NON_PROPERTY_FILTERS.includes(filter.code)

    if (filter.type === 'range') {
      if (filter.selected_from !== null || filter.selected_to !== null) {
        result.push({code: filter.code, property, title: filter.title})
      }
    } else {
      for (const value of (filter as SelectFilter | CheckboxFilter).values) {
        if (value.selected) {
          result.push({
            code: filter.code,
            property,
            title: `${filter.title}: ${value.title}`,
            value: value.value,
          })
        }
      }
    }
  }

  return result
}

function isRangeObject(value: unknown): boolean {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    ('from' in value || 'to' in value)
  )
}

function normalizeRange(value: unknown): {from: number; to: number} {
  const obj = value as Record<string, unknown>
  return {
    from: Number(obj['from'] ?? 0),
    to: Number(obj['to'] ?? 0),
  }
}

function normalizeValueArray(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(String)
  if (typeof value === 'string') return value.split(',').filter(Boolean)
  return []
}
